import { Box, Flex, IconButton, Image, Text, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { CloseIcon } from '@chakra-ui/icons';
import BlockContent from '@sanity/block-content-to-react';
import SDGLogo from 'assets/logos/sdg.png';
import { Tooltip } from 'components';
import { rgba } from 'polished';
import { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { mq } from 'styles/breakpoints';
import useBreakpoint from 'hooks/useBreakpoint';
import BoxHeader from '../BoxHeader/BoxHeader';
import Button from '../Button/Button';
import Carousel from '../Carousel/Carousel';
import ExpandedGoalSlide from './ExpandedGoalSlide/ExpandedGoalSlide';
import GoalSlide from './GoalSlide/GoalSlide';
import { rem } from 'utils/fonts';
import { isCorporateThemeSelector } from 'modules/app/appSlice';
import { allAvailableProjectsSelector } from 'modules/projects/projectsSlice';

const CarouselWrapper = styled.div<{ show?: boolean }>`
  width: 100%;
  padding-bottom: ${(props): string => (props.show ? '30px' : '20px')};
  position: relative;

  .slick-list {
    margin: 0 -8px;
  }

  .slick-slide {
    padding: 0 8px;
    box-sizing: border-box;
  }

  .slick-dots > ul {
    max-width: calc(100% - 50px);
    overflow: hidden;
    display: flex;
    padding-top: 5px;
  }

  ${mq.md} {
    padding-bottom: ${(props): string => (props.show ? '0px' : '20px')};
  }

  ${mq.lg} {
    padding-bottom: 0;
  }
`;

const ExpandedCarouselWrapper = styled(CarouselWrapper)`
  padding-bottom: 0;

  .slick-dots {
    bottom: -60px;

    ${mq.md} {
      bottom: -40px;
    }
  }

  ${mq.md} {
    padding-bottom: 0;

    .slick-list {
      margin-bottom: 25px;
    }
  }
`;

const SupportInfo = styled(Flex)`
  font-weight: 500;
  font-size: ${rem(14)};
  line-height: 125%;

  span {
    font-weight: bold;
  }
`;

const SlideContent = styled(Flex)`
  flex-direction: column;
`;

const MoreButton = styled(Button)`
  margin-top: 25px;
`;

const SlideTitle = styled(Box)`
  font-weight: bold;
  text-transform: uppercase;
  display: flex;
  align-items: center;
`;

const IconWrapper = styled(Box)`
  display: inline-flex;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  align-items: center;
  justify-content: center;

  img {
    border-radius: 50%;
  }
`;

const ExpandedSlide = styled(Flex)<{ show?: boolean }>`
  position: ${(props): string => (props.show ? 'relative' : 'absolute')};
  left: 0;
  top: 0;
  right: 0;
  width: 100%;
  border-radius: 14px;
  min-height: 520px;
  transition: ${(props): string => (props.show ? 'all 0.2s ease-in-out' : 'none')};
  transform: scale(${(props): string => (props.show ? '1' : '0')});
  z-index: ${(props): string => (props.show ? '2' : '-1')};
`;

const ExpandedSlideContent = styled(Flex)`
  flex-direction: column;
  margin: 0 20px 20px 20px;
  width: calc(100% - 40px);

  ${mq.md} {
    margin: 0 24px 25px 48px;
    width: calc(100% - 72px);
  }

  ${mq.lgMinMax} {
    margin: 0 16px 25px 16px;
    width: calc(100% - 32px);
  }
`;

const StyledIconButton = styled(IconButton)`
  height: 30px;
  min-width: 30px;
  max-width: 30px;
  border-radius: 50%;
  margin-left: 5px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);

  svg {
    width: 12px;
  }

  ${mq.md} {
    position: static;
    transform: translateX(0);
  }
`;

const Content = styled.div<{ expanded?: boolean }>`
  ${mq.lg} {
    padding-bottom: ${(props): string => (props.expanded ? '30px' : '22px')};
  }
`;

const Title = styled(Text)`
  display: inline-block;
  margin-right: 15px;
`;

interface GoalSlideType {
  goals: {
    title: string;
    description: SanityRichText[];
    image: string;
  }[];
  image: string;
  gradient: string;
  icon: string;
  title: string;
  description: string;
  expandedDescription: SanityRichText[];
  number: string;
}

interface SDGProps {
  biggerSlides?: boolean;
  sdgs: SustainableDevelopmentGoal[];
  short?: boolean;
}

const SDGContent: FC<SDGProps> = ({ biggerSlides, sdgs, short }) => {
  const { colors } = useTheme();
  const { t } = useTranslation('common');
  const { isMobile, isTablet, isDesktop } = useBreakpoint();
  const [currentSlide, setCurrentSlide] = useState<GoalSlideType | null>(null);
  const { viewableItems: items } = useSelector((state: RootState) => state.projects);
  const allAvailableProjects = useSelector(allAvailableProjectsSelector);
  const isCorporateTheme = useSelector(isCorporateThemeSelector);

  function getProjectsForSdg(sdgId: string): Project[] {
    const availableProjects = isCorporateTheme ? allAvailableProjects : items;
    return availableProjects?.filter(p => p.sdgs?.findIndex(s => s.id === sdgId) > -1) ?? [];
  }
  function getAffectToProject(project: Project, sdgId: string): SanityRichText[] {
    return project.sdgs.find(s => s.id === sdgId)?.affect ?? [];
  }

  const slides: GoalSlideType[] = sdgs.map(s => {
    const sdgProjects = getProjectsForSdg(s.id);
    return {
      goals: sdgProjects.map(p => ({
        title: p.title,
        description: getAffectToProject(p, s.id),
        image: p.imageUrl,
      })),
      description: s.subtitle,
      title: s.title,
      image: s.backgroundUrl,
      expandedDescription: s.description,
      icon: s.logoUrl,
      gradient: s.color,
      number: s.number,
    };
  });
  let numberOfSlides = 3;

  if (!isTablet) {
    numberOfSlides = 2;
  }

  if (isMobile) {
    numberOfSlides = 1;
  }

  if (isDesktop) {
    numberOfSlides = 3;
  }

  const settings = {
    slidesToShow: numberOfSlides,
    slidesToScroll: numberOfSlides,
    draggable: isMobile ? false : true,
    swipeToSlide: isMobile ? true : false,
  };

  const expandedSettings = {
    draggable: isMobile ? false : true,
    swipeToSlide: isMobile ? true : false,
  };

  const selectSlide = (slide: GoalSlideType): void => {
    setCurrentSlide(slide);
  };

  const onCloseIconClick = (): void => {
    setCurrentSlide(null);
  };

  const toGoalSlide = (slide: GoalSlideType): ReactElement => (
    <GoalSlide
      key={slide.title}
      image={slide.image}
      gradient={slide.gradient}
      offset="0 auto"
      slideHeight={short ? '355px' : '488px'}
      bigger={biggerSlides}
    >
      {!short && (
        <SupportInfo color={colors.white} align="flex-end">
          {t('sdg.amountOfProjects')}
          <br />
          {t('sdg.supporting')}&nbsp;<span>({slide.goals.length})</span>
        </SupportInfo>
      )}
      <SlideContent mt="auto" onClick={short ? (): void => selectSlide(slide) : undefined}>
        {slide.icon ? (
          <IconWrapper sx={{ border: `2px solid ${colors.white}`, bg: colors.white, '& img': { bg: colors.white } }}>
            <img src={`${slide.icon}?h=60&auto=format`} alt={slide.title} />
          </IconWrapper>
        ) : null}
        <SlideTitle fontSize={rem(20)} fontFamily="heading" lineHeight="25px" color="white" mt="25px" mb="15px">
          {slide.number}. {slide.title}
        </SlideTitle>
        <Text fontSize={rem(14)} fontWeight={500} lineHeight="135%" color="white">
          {slide.description}
        </Text>
        {!short && (
          <MoreButton
            variant="outline"
            borderColor="white"
            color="white"
            fontSize={rem(15)}
            h="38px"
            minH="38px"
            onClick={(): void => selectSlide(slide)}
          >
            {t('sdg.more')}
          </MoreButton>
        )}
      </SlideContent>
    </GoalSlide>
  );

  return (
    <>
      <BoxHeader>
        <Flex direction="row" justifyContent={isMobile ? 'space-between' : 'flex-start'} alignItems={isMobile ? 'flex-start' : 'center'}>
          <Title layerStyle="projects.SDGTitle" data-testid="sustainable-development-goals">
            {t(isDesktop ? 'sdg.title' : 'sdg.titleShort')}
          </Title>
          <Tooltip aria={t('sdg.tooltipInfo')} label={t('sdg.tooltipContent')} />
        </Flex>
        {!isMobile && !isCorporateTheme ? <Image w="175px" objectFit="contain" src={SDGLogo} alt={t('sdg.logoAlt')} /> : null}
      </BoxHeader>
      <Content expanded={!!currentSlide}>
        <Text layerStyle="projects.SDGSubTitle">{t(`sdg.${isCorporateTheme ? 'corporateDetails' : 'details'}`)}</Text>
        <CarouselWrapper show={!!currentSlide}>
          <Box
            w="100%"
            position={currentSlide ? 'absolute' : 'relative'}
            overflow={currentSlide ? 'hidden' : 'visible'}
            borderRadius="14px"
            opacity={currentSlide ? 0 : 1}
          >
            {slides.length >= numberOfSlides ? (
              <Carousel settings={settings} color={isCorporateTheme ? colors.primary[500] : undefined}>
                {slides.map(toGoalSlide)}
              </Carousel>
            ) : (
              <Flex>{slides.map(toGoalSlide)}</Flex>
            )}
          </Box>
          <ExpandedSlide show={!!currentSlide} bg={colors.white}>
            <Flex
              bg={currentSlide ? rgba(currentSlide.gradient, 0.1) : 'white'}
              w="100%"
              h="100%"
              borderRadius="14px"
              direction={isMobile ? 'column' : 'row'}
            >
              {currentSlide && (
                <>
                  <GoalSlide
                    key={currentSlide.title}
                    image={currentSlide.image}
                    gradient={currentSlide.gradient}
                    slideHeight="auto"
                    slideMinHeight="100%"
                    maxSlideWidth={isMobile ? true : false}
                  >
                    <SlideContent mt="auto">
                      {currentSlide.icon ? (
                        <IconWrapper sx={{ border: `2px solid ${colors.white}`, bg: colors.white, '& img': { bg: colors.white } }}>
                          <img src={currentSlide.icon} alt={currentSlide.title} />
                        </IconWrapper>
                      ) : null}
                      <SlideTitle fontSize={rem(20)} fontFamily="heading" lineHeight="25px" color="white" mt="25px" mb="15px">
                        {currentSlide.title}
                      </SlideTitle>
                      <Box fontSize={rem(14)} fontWeight={500} lineHeight="135%" color="white">
                        <BlockContent blocks={currentSlide.expandedDescription} />
                      </Box>
                    </SlideContent>
                  </GoalSlide>
                  <ExpandedSlideContent
                    maxW="100%"
                    minW={{
                      base: isCorporateTheme ? '150px' : '200px',
                      sm: isCorporateTheme ? '200px' : '250px',
                    }}
                  >
                    <Flex w="100%" align="center" justify="space-between" p={isMobile ? '32px 0 15px 0' : '20px 0 23px 0'}>
                      <SupportInfo color={colors.gray[900]}>
                        <p>
                          {t('sdg.amountOfProjects')} {t('sdg.supporting')}&nbsp;<span>({currentSlide.goals.length})</span>
                        </p>
                      </SupportInfo>
                      {!isMobile && (
                        <StyledIconButton
                          color="gray.900"
                          bg={rgba(colors.secondary['800'], 0.12)}
                          aria-label="Close modal window"
                          icon={<CloseIcon />}
                          onClick={onCloseIconClick}
                          _hover={{ bg: rgba(colors.secondary['800'], 0.3) }}
                        />
                      )}
                    </Flex>
                    <ExpandedCarouselWrapper>
                      <Carousel settings={expandedSettings}>
                        {currentSlide.goals.map(goal => (
                          <ExpandedGoalSlide key={goal.title} title={goal.title} description={goal.description} image={goal.image} />
                        ))}
                      </Carousel>
                      {isMobile && (
                        <StyledIconButton
                          color="secondary.800"
                          bottom={isCorporateTheme ? '-55px' : '-65px'}
                          bg={rgba(colors.secondary['800'], 0.12)}
                          aria-label="Close modal window"
                          icon={<CloseIcon />}
                          onClick={onCloseIconClick}
                          _hover={{ bg: rgba(colors.secondary['800'], 0.3) }}
                        />
                      )}
                    </ExpandedCarouselWrapper>
                  </ExpandedSlideContent>
                </>
              )}
            </Flex>
          </ExpandedSlide>
        </CarouselWrapper>
      </Content>
    </>
  );
};

export default SDGContent;
